import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../ui/theme';
import { Dialog } from '../ui/Modal';
import Label from '../ui/Label';
import Select from '../ui/Select';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import { useQuery } from '@tanstack/react-query';
import http from '../http';
import { hasPerm, isAbortError } from '../utils';
import * as Sentry from '@sentry/browser';
import { toast } from 'react-toastify';
import LoaderBarContext from '../ui/useLoaderBar';
import Checkbox from '../ui/Checkbox';
import Input from '../ui/Input';
import FormGroup from '../ui/FormGroup';
import CurrentUserContext from '../CurrentUserContext';
import LoadingSpinner from '../ui/LoadingSpinner';
import type { SyoApi, SyoLib } from '@tildev-corp/syo-bilan';
import { Prescriptions } from '@tildev-corp/syo-bilan';
import type { UserSyopeType } from '../types/syope';
import { getDateFromISO, getTimeFromISO } from '../utils/luxon';
import { completeStateHtml, displayQualification, isMed, procurationName } from './utilsPrescriptions';
import { BtnTabs } from '../ui/Tabs';
import { alphabeticSort } from './utils';


const ListHeader = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListHeaderItem = styled.li`
  & + & {
    border-top: 1px solid ${theme.grey5};
  }

  & > div {
    padding: ${theme.thin} 0;
  }
`;

const ListPrescription = styled.ul`
  list-style-type: disc;
  padding-top: 10px;
`;

const ListPrescriptionItem = styled.li`
  padding-bottom: 5px;

  & > div {
    padding-left: 20px;
    display: grid;
    grid-template-columns: 65% 30%;
  }
`;

const PseudoLink = styled.span`
  color: #4786ff;
  cursor: pointer;

  &:hover {
    color: #444444;
    text-decoration: underline;
  }
`;

const State = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const Ended = styled.div`
  color: ${theme.green};
`;

export const ErrorStyled = styled.div`
  color: ${theme.red};
`;

export const InProgress = styled.div`
  color: ${theme.orange};
`;

export const Grey = styled.div`
  color: grey;
`;

export const Break = styled.div`
  line-break: anywhere;
`;

export const PrescriptionDetailContent = styled.div`
  display: block;
`;

export const PrescriptionInfo = styled.div`
  display: grid;
  grid-template-columns: 55% 5% 40%;
  position: relative;
`;

export const VDivider = styled.div`
  width: 1px;
  background-color: #333333;
`;

export const SelfCtr = styled.div`
  justify-self: center;
  align-self: center;
`;

export const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const Red = styled.span`
  color: ${theme.red};
`;
const ItalicRed = styled.span`
  color: ${theme.red};

  &:hover {
    text-decoration: underline;
    font-style: italic;
    cursor: pointer;
  }
`;

const StrongRed = styled.strong`
  color: ${theme.red};
  display: flex;
  margin-top: 2rem;
`;

const TabsType = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 50% 50%;
  overflow-y: hidden;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
`;

const CreationContent = styled.div`
  width: 100%;
  border-left: 3px dotted #c6d3db;
  padding-left: 1rem;
`;

const PosologyInputs = styled.div`
  display: grid;
  align-items: end;
  grid-template-columns: 30% 65%;
  gap: 5%;
`;

type Props = {
  bilan: SyoApi.Bilan;
  bilans: any[];
};

type VehiclesType = {
  label: string;
  value: string;
  name: string;
  hasFreePrescriptionsAccess: boolean;
  organization_id: number | null;
};

type PrescriptionSchemaType = {
  value: string;
  label: string;
  protocol: any;
};

type CustomPrescription = {
  id: string;
  therap: boolean;
  med: any;
  quantity: any;
  unit: any;
  route: any;
  geste: any;
  description: any;
};

export default function PrescriptionBilan({ bilan, bilans }: Props) {
  const controller = new AbortController();
  const { currentUser, setUser } = useContext(CurrentUserContext);
  const { loaderBarState, setLoaderBar } = useContext(LoaderBarContext);

  const [prescriptionDetailShow, setPrescriptionDetail] = useState(false);
  const [rppsDialogShow, setRppsDialogShow] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState<SyoLib.Prescription | null>(null);
  const [currentHeader, setCurrentHeader] = useState<SyoApi.PrescriptionHeader | null>(null);
  const [prescriptionsSchema, setPrescriptionsSchema] = useState<PrescriptionSchemaType[]>([]);
  const [prescriptionSchema, setPrescriptionSchema] = useState<PrescriptionSchemaType | null>(null);
  const [vehicle, setVehicle] = useState<VehiclesType | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [check, setCheck] = useState(false);
  const [numeroRpps, setNumeroRpps] = useState('');
  const [rppsIsLoading, setRppsIsLoading] = useState(false);
  const [displayNewPrescription, setDisplayNewPrescription] = useState(bilan.en_cours === 'no');

  const [creationFromSchema, setCreationFromSchema] = useState(true);
  const [customPrescriptionDialogShow, setCustomPrescriptionDialogShow] = useState(false);
  const [customPrescriptionDialogEdition, setCustomPrescriptionDialogEdition] = useState(false);
  const [customPrescriptions, setCustomPrescriptions] = useState<CustomPrescription[]>([]);
  const [currentCustomPrescription, setCurrentCustomPrescription] = useState<CustomPrescription | null>(null);
  const [medicationSolute, setMedicationSolute] = useState(null);
  const [prescTypeTherap, setPrescTypeTherap] = useState(true);
  const [quantity, setQuantity] = useState<string | null>(null);
  const [unit, setUnit] = useState(null);
  const [drugRoute, setDrugRoute] = useState(null);
  const [geste, setGeste] = useState(null);
  const [description, setDescription] = useState(null);

  const { isLoading: medicationsIsLoading, data: medications } = useQuery(
    ['medicationsLists'],
    async () => {
      return await http
        .get(`medications/organization_dictionary.json`, {
          searchParams: {
            organization_ids: vehiclesListFromBilan.map(it => it.organization_id).toString(),
          },
          signal: controller.signal,
        })
        .json()
        .then((res: any) => {
          return new Map(
            (res.organizations ?? []).map((org: any) => [
              org.organization_id,
              alphabeticSort(
                (org.medications ?? []).map((med: any) => ({
                  value: med.name,
                  label: med.name,
                })),
                (value) => value.label,
              ),
            ]),
          );
        })
        .catch(error => {
          if (isAbortError(error)) return;
          console.error(error);
          Sentry.captureException(error);
          toast.warn('Une erreur est survenue lors de la récupération des médicaments');
          throw error;
        });
    },
  );

  const { isLoading: solutesIsLoading, data: solutes } = useQuery(
    ['solutesLists'],
    async () => {
      return await http
        .get(`parameterized_lists/organization_dictionary.json`, {
          searchParams: {
            organization_ids: vehiclesListFromBilan.map(it => it.organization_id).toString(),
          },
          signal: controller.signal,
        })
        .json()
        .then((res: any) => {
          return new Map(
            (res.organizations ?? []).map((org: any) => [
              org.organization_id,
              alphabeticSort(
                (org.lists ?? []).find((s: any) => s.name === 'solutes')?.items.map((sol: any) => ({
                  value: sol.value,
                  label: sol.value,
                })),
                (value) => value.label,
              ),
            ]),
          );
        })
        .catch(error => {
          if (isAbortError(error)) return;
          console.error(error);
          Sentry.captureException(error);
          toast.warn('Une erreur est survenue lors de la récupération des solutés');
          throw error;
        });
    },
  );

  const { isLoading: drugRoutesIsLoading, data: drugRoutes } = useQuery(
    ['drugRoutesLists'],
    async () => {
      return await http
        .get(`drug_routes/organization_dictionary.json`, {
          searchParams: {
            organization_ids: vehiclesListFromBilan.map(it => it.organization_id).toString(),
          },
          signal: controller.signal,
        })
        .json()
        .then((res: any) => {
          return new Map(
            (res.organizations ?? []).map((org: any) => [
              org.organization_id,
              alphabeticSort(
                (org.drug_routes ?? []).map((route: any) => ({
                  value: route.code,
                  label: route.name,
                })),
                (value) => value.label,
              ),
            ]),
          );
        })
        .catch(error => {
          if (isAbortError(error)) return;
          console.error(error);
          Sentry.captureException(error);
          toast.warn('Une erreur est survenue lors de la récupération des voies d\'administrations');
          throw error;
        });
    },
  );

  const { isLoading: unitsIsLoading, data: units } = useQuery(
    ['unitsLists'],
    async () => {
      return await http
        .get(`units/organization_dictionary.json?`, {
          searchParams: {
            organization_ids: vehiclesListFromBilan.map(it => it.organization_id).toString(),
          },
          signal: controller.signal,
        })
        .json()
        .then((res: any) => {
          return new Map(
            (res.organizations ?? []).map((org: any) => [
              org.organization_id,
              alphabeticSort(
                (org.units ?? []).map((unit: any) => ({
                  value: unit.name,
                  label: unit.name,
                  numerator: unit.numerator,
                  denominator: unit.denominator,
                  flowRate: unit.flow_rate,
                  weightDependant: unit.weight_dependant,
                })),
                (value) => value.label,
              ),
            ]),
          );
        })
        .catch(error => {
          if (isAbortError(error)) return;
          console.error(error);
          Sentry.captureException(error);
          toast.warn('Une erreur est survenue lors de la récupération des unités');
          throw error;
        });
    },
  );

  const fetchPrescriptionsSchema = async (vehicle: VehiclesType): Promise<PrescriptionSchemaType[]> => {
    if (!currentUser || !vehicle) return [];
    const authorId = bilans?.find(b => `${b.equipe.vehicule} (${b.author.organization.name})` === vehicle.value)?.author_id;
    if (!authorId) return [];

    try {
      const res = await http
        .get(`organizations/${encodeURIComponent(currentUser.organization_id)}/prescription_schema.json`, {
          signal: controller.signal,
          searchParams: {
            author_id: authorId,
          },
        })
        .json<SyoApi.GetPrescriptionsSchemaResponse>();
      res.protocols.flatMap(it => it.schemas).forEach(it => (it.payloadParse = JSON.parse(it.payload)));
      return res.protocols.map(it => {
        const ageRange = it.age_range === 'CHILD' ? 'Pédiatrique' : 'Adulte';
        return {
          value: `${it.name} (${ageRange})`,
          label: `${it.name} (${ageRange})`,
          protocol: it,
        };
      });
    } catch (error) {
      if (isAbortError(error)) return [];
      console.error(error);
      Sentry.captureException(error);
      toast.warn('Une erreur est survenue lors de la récupération de la liste des schémas de prescriptions');
      throw error;
    }
  };

  const {
    isLoading: prescriptionsIsLoading,
    data: prescriptionsHeaders,
    refetch: refetchPrescriptions,
  } = useQuery(
    ['prescriptionsActiveList'],
    async () => {
      try {
        const res = await http
          .get(`interventions/${encodeURIComponent(bilan.intervention_id ?? '')}/active_prescriptions.json`, {
            signal: controller.signal,
          })
          .json<SyoApi.GetActivePrescriptionsResponse>();
        const temp: SyoLib.PrescriptionHeader[] = [];
        if (res.headers) {
          res.headers.flatMap(it => it.prescriptions);
          res.headers.forEach(e => temp.push(new Prescriptions(e, bilan)));
          return temp;
        }
      } catch (error) {
        if (isAbortError(error)) return;
        console.error(error);
        Sentry.captureException(error);
        toast.warn('Une erreur est survenue lors de la récupération des prescriptions actives');
        throw error;
      }
    },
    {
      refetchInterval: 1000,
      cacheTime: 0,
      enabled: bilan !== null && bilan !== undefined,
    },
  );

  const setPrescriptionDetailShow = (p: SyoLib.Prescription | null, h: SyoLib.PrescriptionHeader | null) => {
    if (!prescriptionDetailShow) {
      setCurrentHeader(h);
      setCurrentPrescription(p);
      if (p && h) {
        setPrescriptionDetail(!prescriptionDetailShow);
      }
    } else {
      setPrescriptionDetail(!prescriptionDetailShow);
    }
  };

  const stateColorHtml = (p: SyoLib.Prescription) => {
    switch (p?.last_prescription_status?.state) {
      case 'IN_PROGRESS':
        return <InProgress>{p.stateDisplay}</InProgress>;
      case 'REFUSED':
        return <ErrorStyled>{p.stateDisplay}</ErrorStyled>;
      case 'CANCELED':
        return <ErrorStyled>{p.stateDisplay}</ErrorStyled>;
      case 'COMPLETED':
        return <Ended>{p.stateDisplay}</Ended>;
      default:
        return <span>{p.stateDisplay}</span>;
    }
  };

  const handleSetVehicle = async (v: VehiclesType) => {
    setVehicle(v);
    setPrescriptionSchema(null);

    try {
      setPrescriptionsSchema(await fetchPrescriptionsSchema(v));
    } catch (e) {
      setPrescriptionsSchema([]);
    }
  };

  const vehiclesListFromBilan = useMemo(() => {
    const vehicles: VehiclesType[] = [];
    bilan?.vehicles?.slice()?.reverse()?.forEach(it => {
      const currentVehicleKey = `${it.name} (${it.performer})`;
      let organization_id: number | null = null;
      const permissions = bilans
        ?.filter(({ source, equipe, author }: any) =>
          source === 'ANDROID' &&
          `${equipe.vehicule} (${author.organization.name})` === currentVehicleKey,
        )
        .flatMap(({ author }: any) => {
          organization_id = author.organization_id;
          return author.permissions.map((p: any) => p.key);
        }) ?? [];

      const hasAndroidAccessPrescriptions = permissions.some((p: any) =>
        (p.includes('assessment:prescriptions') || p.includes('assessment:prescriptions:infirmier-libre'))
        && !p.includes('assessment:prescriptions:mode-degrade:asu'),
      );

      const hasFreePrescriptionsAccess = permissions.some((p: any) =>
        p.includes('assessment:prescriptions:infirmier-libre'),
      );

      if (vehicles.findIndex(v => v.label === `${it.name} (${it.performer})`) === -1
        && hasAndroidAccessPrescriptions
      ) {
        vehicles.push({
          label: `${it.name} (${it.performer})`,
          value: `${it.name} (${it.performer})`,
          name: it.name,
          hasFreePrescriptionsAccess: hasFreePrescriptionsAccess ?? false,
          organization_id: organization_id,
        });
      }
    });
    if (vehicles?.length === 1) handleSetVehicle(vehicles[0]);
    console.log(vehicles);
    return vehicles;
  }, [bilan, bilans]);

  const disablePrescrire = useMemo(() => {
    if (creationFromSchema) {
      return !vehicle || !prescriptionSchema || !check;
    } else {
      return !vehicle || customPrescriptions === null || customPrescriptions?.length < 1 || !check;
    }
  }, [creationFromSchema, vehicle, prescriptionSchema, check, customPrescriptions]);

  const { refetch: refetchUser } = useQuery(
    ['user'],
    async () => {
      if (!currentUser) return;
      return await http
        .get(`user/me`, {
          signal: controller.signal,
        })
        .json<UserSyopeType>()
        .then(res => {
          return res;
        })
        .catch(error => {
          if (isAbortError(error)) return;
          console.error(error);
          Sentry.captureException(error);
          toast.warn('Une erreur est survenue lors de la récupération de l\'utilisateur');
          throw error;
        });
    },
    {
      cacheTime: 0,
    },
  );

  const handleSubmitRpps = async (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    if (!currentUser) return;
    setLoaderBar(true);
    setRppsIsLoading(true);
    try {
      await http.post(`user/update_rpps.json`, { searchParams: { numero_rpps: numeroRpps } }).json();
      refetchUser().then(res => {
        if (!res.data) throw 'Prescription refetch empty';
        currentUser.numero_rpps = res.data.numero_rpps;
        setUser(currentUser);
        localStorage.removeItem('nfCurrentUser');
        localStorage.setItem('nfCurrentUser', JSON.stringify(currentUser));
        setLoaderBar(false);
      });
      setRppsDialogShow(false);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      toast.error(
        'Impossible d\'ajouter ce numéro RPPS à votre utilisateur. Vérifiez votre connexion internet ou contactez l\'assistance Syopé.',
      );
    } finally {
      setLoaderBar(false);
      setRppsIsLoading(false);
    }
  };

  const handleCanceled = async (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();

    try {
      await http
        .post(
          `interventions/${encodeURIComponent(bilan.intervention_id ?? '')}/prescriptions/${encodeURIComponent(currentPrescription?.id ?? '')}/edit_prescription_status.json`,
          { searchParams: { state: 'CANCELED', comment: '', observed_at: new Date().toString() } },
        )
        .json();
      setPrescriptionDetailShow(null, null);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      toast.error(
        'Impossible d\'annuler cette prescription. Vérifiez votre connexion internet ou contactez l\'assistance Syopé.',
      );
    } finally {
      setLoaderBar(false);
    }
  };

  const handlePrescrire = async (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();

    if (loaderBarState === true || !currentUser) return;
    setLoaderBar(true);
    try {
      await http
        .post(`interventions/${encodeURIComponent(bilan.intervention_id ?? '')}/create_prescription.json`, {
          searchParams: {
            vehicle_target: vehicle?.name ?? '',
            numero_rpps: currentUser.numero_rpps ?? '',
            name: creationFromSchema ? prescriptionSchema?.protocol?.name ?? '' : 'Hors protocole',
            age_range: creationFromSchema ? prescriptionSchema?.protocol?.age_range ?? '' : 'UNKNOWN',
            comment: creationFromSchema ? prescriptionSchema?.protocol?.comment ?? '' : '',
          },
        })
        .json<{ id: string }>()
        .then(async res => {
          await http
            .post(
              `interventions/${encodeURIComponent(bilan.intervention_id ?? '')}/prescription_headers/${encodeURIComponent(res.id)}/add_prescription_content.json`,
              {
                json: {
                  prescriptions: creationFromSchema
                    ? (prescriptionSchema?.protocol?.schemas ?? []).map((it: any) => {
                      return {
                        payload: it?.payload,
                        prescription_type: it?.prescription_type ?? '',
                        comment: commentaire ?? '',
                      };
                    })
                    : (customPrescriptions ?? []).map((it: CustomPrescription) => {
                      return {
                        payload: JSON.stringify(it.therap ? {
                          label: it.med,
                          dosage: it.quantity,
                          route: it.route?.label,
                          unit: it.unit?.label,
                        } : {
                          geste: it.geste,
                          description: it.description,
                        }),
                        prescription_type: it.therap ? 'MEDICATION' : 'GESTURE',
                        comment: commentaire ?? '',
                      };
                    }),
                },
              },
            )
            .json()
            .then(() => {
              setDisplayNewPrescription(false);
              setCustomPrescriptions([]);
              setPrescriptionSchema(null);
              setCommentaire('');
              setCheck(false);
              refetchPrescriptions();
            });
        });
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      toast.error(
        'Impossible de créer cette prescription. Vérifiez votre connexion internet ou contactez l\'assistance Syopé.',
      );
    } finally {
      setLoaderBar(false);
    }
  };

  const onCheckChange = () => {
    setCheck(!check);
  };

  const getSchemaDescription = (schema: any): string => {
    if (schema.prescription_type === 'GESTURE') {
      return schema.payloadParse.geste;
    } else {
      return schema.payloadParse.dosage
        ? `${schema.payloadParse.label} (${schema.payloadParse.dosage} ${schema.payloadParse.unit} ${schema.payloadParse.route})`
        : `${schema.payloadParse.label} (${schema.payloadParse.route})`;
    }
  };

  const getCustomPrescDescription = (custom: CustomPrescription): string => {
    if (!custom.therap) {
      return custom.description !== null && custom.description !== ''
        ? `${custom.geste} (${custom.description})` : `${custom.geste}`;
    } else {
      return `${custom.med} (${custom.quantity} ${custom.unit?.label} ${custom.route?.label})`;
    }
  };

  const tabsBtnCreationTypeOptions = [
    {
      option: true,
      text: 'Schéma de prescription',
    },
    {
      option: false,
      text: 'Personnalisée',
    },
  ];

  const tabsBtnPrescTypeOptions = [
    {
      option: true,
      text: 'Thérapeutique',
    },
    {
      option: false,
      text: 'Geste',
    },
  ];

  const handleTabsCreationTypeChange = (value: any) => {
    setCreationFromSchema(value);
  };

  const handleTabsPrescTypeViewChange = (value: any) => {
    setPrescTypeTherap(value);
  };

  const canNotAdd = () => {
    if (prescTypeTherap) {
      return medicationSolute === null || quantity === null || unit === null || drugRoute === null;
    } else {
      return geste === null || geste === '';
    }
  };

  const add = () => {
    customPrescriptions.push(
      {
        id: crypto.randomUUID(),
        therap: prescTypeTherap,
        med: medicationSolute,
        quantity: quantity,
        unit: unit,
        route: drugRoute,
        geste: geste,
        description: description,
      } as CustomPrescription,
    );

    clearDialog()
    toggleCustomPrescriptionDialog(false);
  };

  const deleteP = () => {
    customPrescriptions.splice(
      customPrescriptions.findIndex(it => it.id === currentCustomPrescription?.id),
      1
    )
    clearDialog()
    toggleCustomPrescriptionDialog(false);
  };

  const edit = () => {
    const p = customPrescriptions.find(it => it.id === currentCustomPrescription?.id)
    if (!p) return;

    p.therap = prescTypeTherap;
    p.med = medicationSolute;
    p.quantity = quantity;
    p.unit = unit;
    p.route = drugRoute;
    p.geste = geste;
    p.description = description;

    clearDialog()
    toggleCustomPrescriptionDialog(false);
  };

  const clearDialog = () => {
    setMedicationSolute(null);
    setPrescTypeTherap(true);
    setQuantity(null);
    setUnit(null);
    setDrugRoute(null);
    setGeste(null);
    setDescription(null);
  };

  const onDrugChange = (value: any) => {
    setMedicationSolute(value);
  };

  const toggleCustomPrescriptionDialog = (stateOpen: any, currentCustomPresc: CustomPrescription | null = null) => {
    setCustomPrescriptionDialogShow(stateOpen);

    if (currentCustomPresc != null) {
      setCustomPrescriptionDialogEdition(true);
      setCurrentCustomPrescription(currentCustomPresc);
      setMedicationSolute(currentCustomPresc.med);
      setPrescTypeTherap(currentCustomPresc.therap);
      setQuantity(currentCustomPresc.quantity);
      setUnit(currentCustomPresc.unit);
      setDrugRoute(currentCustomPresc.route);
      setGeste(currentCustomPresc.geste);
      setDescription(currentCustomPresc.description);
    } else {
      setCustomPrescriptionDialogEdition(false);
      setCurrentCustomPrescription(null);
    }
  };

  const getMedicOptions = () => {
    const medicOptions = [];
    if (medications && solutes) {
      medicOptions.push(...(vehicle?.organization_id
        ? (medications as Map<number, VehiclesType[]>)?.get(vehicle.organization_id as number) ?? []
        : []));
      medicOptions.push(...(vehicle?.organization_id
        ? (solutes as Map<number, VehiclesType[]>)?.get(vehicle.organization_id as number) ?? []
        : []));
    }

    return medicOptions;
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3>Prescriptions / ASU</h3>
      <hr />
      <ListHeader>
        {!prescriptionsIsLoading ? (
          prescriptionsHeaders?.map((header, idx) => (
            <ListHeaderItem key={idx}>
              <State>
                N° {prescriptionsHeaders?.length - idx} |
                {header.procuration ? ` ${procurationName(header)}` : ` Docteur ${header.user.full_name}`}
              </State>
              {header.name && header.name != '' &&
                <Grey>{header.name}<br />{header.comment && header.comment != '' && header.comment}</Grey>
              }
              <ListPrescription>
                {header.prescriptions.map((prescription, pIdx) => (
                  <ListPrescriptionItem key={pIdx}>
                    <State>
                      <PseudoLink onClick={() => setPrescriptionDetailShow(prescription, header)}>
                        {prescription.label}
                      </PseudoLink>
                      {stateColorHtml(prescription)}
                    </State>
                    <Grey>{prescription.commentDisplay}</Grey>
                  </ListPrescriptionItem>
                ))}
              </ListPrescription>
            </ListHeaderItem>
          ))
        ) : (
          <LoadingSpinner className='center vh-50' />
        )}
        <Dialog
          isOpen={prescriptionDetailShow}
          toggleDialog={() => setPrescriptionDetailShow(null, null)}
          title={
            (currentHeader?.asu ? 'ASU' : 'Prescription')
            + ' de '
            + (isMed(currentPrescription) ? currentPrescription?.payloadParse.label : currentPrescription?.payloadParse.geste)
          }
          children={
            <PrescriptionDetailContent>
              <Break>
                <br />
                {currentHeader?.shared_at ? (
                  <Grey>
                    {
                      (!currentHeader?.procuration ? 'Envoyée par ' : 'Créée sur les lieux de l\'intervention à l\'initiative de ') +
                      (!currentHeader?.procuration ? currentHeader?.user?.full_name : procurationName(currentHeader)) +
                      ' au ' +
                      currentHeader?.vehicle_target +
                      ' à ' +
                      getTimeFromISO(currentHeader.shared_at, 'TIME_24_SIMPLE') +
                      ' le ' +
                      getDateFromISO(currentHeader.shared_at, 'DATE_SHORT')}
                  </Grey>
                ) : (
                  <Grey>{bilan.en_cours !== 'no' ? 'Envoi en cours...' : 'Non approuvée en intervention'}</Grey>
                )}
                <br />
                <span>{completeStateHtml(currentHeader, currentPrescription)}</span>
                <br />
                <br />
                <PrescriptionInfo>
                  <SelfCtr>{currentPrescription?.commentDisplay}</SelfCtr>
                  <VDivider></VDivider>
                  {isMed(currentPrescription) ? (
                    <FlexCol>
                      <Grey>{currentHeader?.name}</Grey>
                      <span>{currentPrescription?.payloadParse.label}</span>
                      <span>{currentPrescription?.payloadParse.dosage} {currentPrescription?.payloadParse.unit}</span>
                      <span>{currentPrescription?.payloadParse.route}</span>
                    </FlexCol>
                  ) : (
                    <FlexCol>
                      <Grey>{currentHeader?.name}</Grey>
                      <span>{currentPrescription?.payloadParse.geste}</span>
                      <span>{currentPrescription?.payloadParse.description}</span>
                    </FlexCol>
                  )}
                </PrescriptionInfo>
                <br />
                {currentPrescription?.numero_kit && (<>
                  <Grey>Numéro de kit utilisé : {currentPrescription?.numero_kit}</Grey>
                  <br />
                </>)}
                {currentHeader?.prescription_qualification && displayQualification(currentHeader.prescription_qualification)}
                {bilan.en_cours !== 'no' && !currentHeader?.asu &&
                !hasPerm(currentUser, 'web:intervention:prescriptions:disabled-actions') &&
                currentPrescription?.last_prescription_status?.state !== 'CANCELED' &&
                currentPrescription?.last_prescription_status?.state !== 'COMPLETED' &&
                currentPrescription?.last_prescription_status?.state !== 'REFUSED' ? (
                  <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                      className='warn'
                      style={{ marginBottom: '.5rem', marginTop: '.5rem' }}
                      onClick={ev => handleCanceled(ev)}
                      type='submit'>Annuler la prescription</Button>
                  </div>
                ) : ('')}
              </Break>
            </PrescriptionDetailContent>
          }
        />
      </ListHeader>
      {!displayNewPrescription &&
        (vehiclesListFromBilan?.length > 0 ?
          (<Button
            style={{ width: '100%', marginTop: '1rem' }}
            onClick={() => setDisplayNewPrescription(!displayNewPrescription)}
            type='submit'>Nouvelle prescription</Button>) :
          <StrongRed>Aucun véhicule n'est habilité à recevoir une prescription via ce module.
            <br />Merci de privilégier un contact téléphonique.
          </StrongRed>)
      }
      {bilan?.en_cours !== 'no'
        && !hasPerm(currentUser, 'web:intervention:prescriptions:disabled-actions')
        && displayNewPrescription && (
          <>
            {!rppsIsLoading ? (
              <>
                <br />
                <br />
                <h3>Créer une prescription</h3>
                <hr />
                {vehiclesListFromBilan?.length > 1 ? <>
                  <Label htmlFor='vehicle_select'>Sélectionnez un véhicule</Label>
                  {vehiclesListFromBilan?.map((v) => <Button
                    className={vehicle?.value === v.value ? 'like-disabled validated' : 'like-disabled'}
                    style={{ width: '100%', marginTop: '0.25rem', marginBottom: '0.25rem' }}
                    onClick={() => handleSetVehicle(v)}
                    type='submit'>{v.label}</Button>,
                  )}
                </> : (<span>Véhicule ciblé : <strong>{vehicle?.label}</strong></span>)}
                <br />
                <br />
                {vehicle?.hasFreePrescriptionsAccess && <TabsType>
                  {tabsBtnCreationTypeOptions.map((btn, idx) => (
                    <BtnTabs
                      key={idx}
                      onClick={() => handleTabsCreationTypeChange(btn.option)}
                      className={`tabButton${creationFromSchema === btn.option ? ' active' : ''}`}
                    >
                      {btn.text}
                    </BtnTabs>
                  ))}
                </TabsType>}

                {creationFromSchema ? (
                  <CreationContent>
                    <Label htmlFor='prescription_select'>Sélectionnez une prescription</Label>
                    <Select
                      id={'prescription_select'}
                      placeholder={'Sélectionnez une prescription'}
                      isDisabled={!vehicle}
                      options={prescriptionsSchema}
                      value={prescriptionSchema}
                      onChange={(obj: PrescriptionSchemaType) => setPrescriptionSchema(obj)}
                    />
                    {!vehicle && <Red style={{ fontSize: '0.8rem' }}>Veuillez sélectionner un véhicule.</Red>}
                    <br />
                    {prescriptionSchema && <Grey>
                      <ul>
                        {prescriptionSchema.protocol.schemas.map((it: any) => (
                          <li>{getSchemaDescription(it)}</li>
                        ))}
                      </ul>
                    </Grey>
                    }
                    <br />
                    <br />
                    <Label htmlFor='prescription_commentaire'>Commentaire</Label>
                    <Textarea id='prescription_commentaire' rows={5}
                              onChange={ev => setCommentaire(ev.target.value)} />
                  </CreationContent>
                ) : (
                  <CreationContent>
                    <br />
                    {customPrescriptions?.length > 0 ? (<Grey>
                      <ul>
                        {customPrescriptions.map((it: any) => (
                          <li><PseudoLink onClick={ev => toggleCustomPrescriptionDialog(true, it)}>
                            {getCustomPrescDescription(it)}</PseudoLink></li>
                        ))}
                      </ul>
                    </Grey>) : (<Grey>Aucune prescription</Grey>)
                    }
                    <br />
                    <Button
                      style={{ marginBottom: '.5rem', marginTop: '.5rem', width: '100%' }}
                      onClick={ev => toggleCustomPrescriptionDialog(true)}
                      type='submit'>→ Ajouter une prescription à la liste</Button>
                    <br />
                    <br />
                    <Label htmlFor='prescription_commentaire'>Commentaire</Label>
                    <Textarea id='prescription_commentaire' rows={5}
                              onChange={ev => setCommentaire(ev.target.value)} />
                  </CreationContent>
                )}
                <br />
                <br />
                <br />

                {currentUser?.numero_rpps ? (
                  <>
                    {rppsIsLoading ? (<LoadingSpinner className='center' />) : (
                      <>
                        <Checkbox
                          checked={check}
                          onChange={onCheckChange}
                          disabled={false}
                          label={`Je certifie, ${currentUser.full_name}, que le n° RPPS "${currentUser.numero_rpps}" est bien le mien.\nPour information, cette donnée sera transmise aux services juridique de votre organisation en cas de recours légal.`} />
                        <ItalicRed style={{ fontSize: '0.8rem' }}
                                   onClick={() => setRppsDialogShow(true)}>
                          Ce numéro RPPS n'est pas le mien
                        </ItalicRed>
                      </>
                    )}
                  </>
                ) : (
                  <ItalicRed onClick={() => setRppsDialogShow(true)}>
                    Aucun n° RPPS renseigné, veuillez le renseigner ici
                  </ItalicRed>
                )}

                <br />
                <br />
                <span>Compte utilisé :{' '}
                  <strong>{currentUser?.full_name} ({currentUser?.organization.name})</strong>
                            </span>
                <br />
                <br />
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
                  <Button
                    className={'warn'}
                    style={{ marginBottom: '.5rem', marginTop: '.5rem', width: '100%' }}
                    onClick={() => setDisplayNewPrescription(!displayNewPrescription)}
                    type='submit'>Annuler</Button>
                  <Button
                    style={{ marginBottom: '.5rem', marginTop: '.5rem', width: '100%' }}
                    onClick={ev => handlePrescrire(ev)}
                    disabled={disablePrescrire}
                    type='submit'>Prescrire</Button>
                </div>
              </>
            ) : (<LoadingSpinner className='center vh-50' />)}
          </>
        )}
      <Dialog
        isOpen={rppsDialogShow}
        toggleDialog={() => setRppsDialogShow(!rppsDialogShow)}
        title={'Saisie de votre numéro RPPS'}
        children={
          <PrescriptionDetailContent>
            <Break>
              <br />
              <FormGroup>
                <Label htmlFor='user-matricule-input'>N° RPPS (Médecin uniquement)</Label>
                <Input id='user-rpps-input' value={numeroRpps}
                       onChange={ev => setNumeroRpps(ev.target.value)} />
              </FormGroup>
              <div style={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                  style={{ marginBottom: '.5rem', marginTop: '.5rem' }}
                  onClick={ev => handleSubmitRpps(ev)}
                  disabled={numeroRpps === null || numeroRpps.length < 1}
                  type='submit'>Enregistrer mon numéro</Button>
              </div>
            </Break>
          </PrescriptionDetailContent>
        }
      />
      <Dialog
        isOpen={customPrescriptionDialogShow}
        toggleDialog={() => toggleCustomPrescriptionDialog(!customPrescriptionDialogShow)}
        title={'Ajouter une prescription'}
        children={
          <PrescriptionDetailContent>
            <Break>
              <br />
              <FormGroup>
                <TabsType>
                  {tabsBtnPrescTypeOptions.map((btn, idx) => (
                    <BtnTabs
                      key={idx}
                      onClick={() => handleTabsPrescTypeViewChange(btn.option)}
                      className={`tabButton${prescTypeTherap === btn.option ? ' active' : ''}`}
                    >
                      {btn.text}
                    </BtnTabs>
                  ))}
                </TabsType>
                <br />

                {prescTypeTherap ? (
                  <>
                    <>
                      {!medicationsIsLoading && medications && (
                        <div>
                          <Label htmlFor='filter-injection-input-drug'>Sélectionnez une drogue</Label>
                          <Select
                            id='filter-injection-input-drug'
                            isSearchable={true}
                            isClearable={true}
                            options={getMedicOptions()}
                            value={((medications as Map<number, any[]>)?.get(vehicle?.organization_id as number) ?? []).find((it: any) => it.value === medicationSolute)}
                            onChange={(ev: any) => onDrugChange(ev?.value ?? '')}
                            placeholder='Sélectionnez une drogue'
                          />
                        </div>
                      )}
                    </>
                    <br />
                    <PosologyInputs>
                      <div>
                        <Label htmlFor='dose-input'>Dose</Label>
                        <Input
                          id='dose-input'
                          type='number'
                          inputMode='numeric'
                          placeholder='Dose'
                          value={quantity ?? ''}
                          onChange={(ev: any) => setQuantity(ev.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor='unit-input'>Sélectionnez une unité</Label>
                        <Select
                          id='unit-input'
                          isSearchable={true}
                          isClearable={true}
                          placeholder='Unité'
                          options={vehicle?.organization_id
                            ? (units as Map<number, VehiclesType[]>)?.get(vehicle.organization_id as number) ?? []
                            : []}
                          value={unit}
                          onChange={(ev: any) => setUnit(ev)}
                        />
                      </div>
                    </PosologyInputs>
                    <br />
                    <div>
                      <Label htmlFor='drug-routes-input'>Sélectionnez une voie d'administration</Label>
                      <Select
                        id='drug-routes-input'
                        isSearchable={true}
                        isClearable={true}
                        options={vehicle?.organization_id
                          ? (drugRoutes as Map<number, VehiclesType[]>)?.get(vehicle.organization_id as number) ?? []
                          : []}
                        value={drugRoute}
                        onChange={(ev: any) => setDrugRoute(ev)}
                        placeholder="Voie d'administration"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor='geste-input'>Geste</Label>
                      <Input
                        id='geste-input'
                        placeholder='Geste'
                        value={geste ?? ''}
                        onChange={(ev: any) => setGeste(ev.target.value)}
                      />
                    </div>
                    <br />
                    <div>
                      <Label htmlFor='description-input'>Description</Label>
                      <Textarea id='description-input'
                                rows={5}
                                placeholder='Description'
                                value={description ?? ''}
                                onChange={(ev: any) => setDescription(ev.target.value)} />
                    </div>
                  </>
                )}
              </FormGroup>
              <br />
              <br />
              <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem' }}>
                {!customPrescriptionDialogEdition && <Button
                  style={{ marginBottom: '.5rem', marginTop: '.5rem' }}
                  onClick={(ev: any) => {
                    add();
                  }}
                  disabled={canNotAdd()}
                  type='submit'>Ajouter la prescription</Button>}
                {customPrescriptionDialogEdition && <Button
                  style={{ marginBottom: '.5rem', marginTop: '.5rem' }}
                  className='warn'
                  onClick={(ev: any) => {
                    deleteP();
                  }}>Supprimer la prescription</Button>}
                {customPrescriptionDialogEdition && <Button
                  style={{ marginBottom: '.5rem', marginTop: '.5rem' }}
                  onClick={(ev: any) => {
                    edit();
                  }}
                  disabled={canNotAdd()}
                  type='submit'>Modifier la prescription</Button>}
              </div>
            </Break>
          </PrescriptionDetailContent>
        }
      />
    </div>
  );
}
